import { ReactNode, useState, useRef, useCallback, memo, useMemo } from 'react';
import { CalendarDay } from './CalendarDay';
import Stack from '@mui/material/Stack';
import {
  DateString,
  EventKeyed,
  useEventsLazy,
} from 'src/contexts/EventsLazyContext';
import { useNumVisibleColumns } from 'src/hooks/calendar/useNumVisibleColumns';
import { useTheme } from '@mui/material/styles';
import { GradientTypography } from 'src/components/gradients/GradientTypography';
import { GliderContainer, ReactGlider } from '../../ReactGlider';
import { CalendarHeader } from '../CalendarHeader';
import {
  shiftDate,
  toDateString,
} from '../../../../functions/src/util/dates/Dates';
import { BaseError } from '../../error/BaseError';
import { toLocalMidnightDate } from '../../../../functions/src/util/date/paramsUtc';
import { OptionalWrapper } from '../../OptionalWrapper';

export type CalendarDailyProps = {
  Title?: ReactNode | string;
  height?: string;
  ssrColumnsVisible?: number;
  initialDate?: Date;
  calendarHeaderOptions?: {
    showCreateButton: boolean;
  };
};

export const MIN_FETCH = 10 as const;
export const LEFT_FETCH_MULTIPLIER = 3;
export const MS_PER_DAY = 1000 * 60 * 60 * 24;
export const CALENDAR_COLUMN_WIDTH = 296;

function CalendarDailyUnmemoized({
  Title,
  height = '100%',
  ssrColumnsVisible = 7,
  initialDate = new Date(),
  calendarHeaderOptions = {
    showCreateButton: false,
  },
}: CalendarDailyProps) {
  const theme = useTheme();
  const { events, fetchAtLeast, fetchMoreWithoutSet, clearEvents, push } =
    useEventsLazy();
  const { numVisibleColumns, firstEventRef } = useNumVisibleColumns();

  const sortedEvents = Object.entries(events).sort(
    ([dateStringA], [dateStringB]) => {
      return new Date(dateStringA).getTime() - new Date(dateStringB).getTime();
    },
  );

  const fetchLeft = async () => {
    if (sortedEvents.length === 0) return;

    const leftmostVisibleDate = new Date(sortedEvents[0][0]);

    const shiftAmount = Math.min(
      -MIN_FETCH,
      -numVisibleColumns * LEFT_FETCH_MULTIPLIER,
    );

    const startDate = shiftDate(leftmostVisibleDate, shiftAmount);
    const endDate = shiftDate(leftmostVisibleDate, -1);

    const { count } = await fetchAtLeast(
      toDateString(startDate),
      toDateString(endDate),
      'before',
    );

    gliderRef.current?.shiftCursor(count, 'instant');
  };

  const fetchRight = async () => {
    if (sortedEvents.length === 0) return;

    const rightmostVisibleDate = new Date(
      sortedEvents[sortedEvents.length - 1][0],
    );

    const endDateShift = Math.max(MIN_FETCH, numVisibleColumns);

    const startDate = shiftDate(rightmostVisibleDate, 1);
    const endDate = shiftDate(rightmostVisibleDate, endDateShift);

    await fetchAtLeast(toDateString(startDate), toDateString(endDate), 'after');
  };

  const gliderRef = useRef<GliderContainer>(null);

  const [lockFetch, setLockFetch] = useState(false);

  const bufferBeforeFetched = Math.floor(ssrColumnsVisible / 2);

  const lazyLoad = (cursor: number) => {
    const bufferEvents = async () => {
      if (lockFetch) {
        return;
      }
      if (cursor < bufferBeforeFetched) {
        setLockFetch(true);
        await fetchLeft();
        setLockFetch(false);
      } else if (
        cursor >
        sortedEvents.length - numVisibleColumns - bufferBeforeFetched
      ) {
        setLockFetch(true);
        await fetchRight();
        setLockFetch(false);
      }
    };
    bufferEvents();
  };

  const shiftLeft = useCallback(() => {
    gliderRef.current?.shiftCursor(-1);
  }, [gliderRef]);

  const shiftRight = useCallback(() => {
    gliderRef.current?.shiftCursor(1);
  }, [gliderRef]);

  const shiftLeftMuch = useCallback(() => {
    gliderRef.current?.shiftCursor(-numVisibleColumns);
  }, [gliderRef, numVisibleColumns]);

  const shiftRightMuch = useCallback(() => {
    gliderRef.current?.shiftCursor(numVisibleColumns);
  }, [gliderRef, numVisibleColumns]);

  const findIndexOf = (
    eventsNew: Record<string, EventKeyed[]>,
    selectedDate: DateString,
  ): number => {
    const selectedDateObj = new Date(selectedDate);
    const maxTries = Object.keys(eventsNew).length;
    for (let i = 0; i <= maxTries; i++) {
      const index = Object.keys(eventsNew).findIndex((dateString) => {
        return dateString === toDateString(selectedDateObj);
      });

      if (index !== -1) return index;

      selectedDateObj.setDate(selectedDateObj.getDate() - 1);
    }

    return -1;
  };

  const [currentDate, setCurrentDate] = useState<Date>(initialDate);

  const selectDate = async (selectedDate: DateString) => {
    const selectedDateObj = new Date(selectedDate);
    const startDate = shiftDate(selectedDateObj, -numVisibleColumns * 2);
    const endDate = shiftDate(selectedDateObj, numVisibleColumns * 3);

    const { eventsNew } = await fetchMoreWithoutSet(
      toDateString(startDate),
      toDateString(endDate),
    );
    clearEvents();
    push(eventsNew);

    setCurrentDate(new Date(selectedDate));

    const selectedDateIndex = findIndexOf(eventsNew, selectedDate);
    gliderRef.current?.setCursor(
      selectedDateIndex === -1 ? 0 : selectedDateIndex,
    );
  };

  const cursorInitial = useMemo(() => {
    return findIndexOf(events, toDateString(initialDate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDate]);

  const isCurrentDay = (dateString: DateString): boolean => {
    const today = toDateString(new Date(Date.now()));

    return dateString === today;
  };

  return (
    <Stack
      borderRadius="10px"
      width="100%"
      sx={{
        p: '8px',
        background: theme.palette.background.elevation[8],
      }}
    >
      {Title && typeof Title === 'string' ? (
        <GradientTypography
          textTransform="uppercase"
          variant="h5"
          sx={{ px: 2, pb: 2 }}
          gradientColor={'primary.vertical'}
        >
          {Title}
        </GradientTypography>
      ) : (
        Title
      )}
      <CalendarHeader
        onLeft={shiftLeft}
        onRight={shiftRight}
        onDoubleLeft={shiftLeftMuch}
        onDoubleRight={shiftRightMuch}
        selectDate={selectDate}
        visibleDate={currentDate}
        {...calendarHeaderOptions}
      />
      <Stack
        height={height}
        overflow="auto"
        sx={theme.palette.scrollbars.invisible}
      >
        {sortedEvents.length === 0 && (
          <BaseError
            imgUrl="/assets/images/mascots/mascot-crying.png"
            errorTitle="NO EVENTS FOUND"
            description="Uh oh! Check out more recent events!"
            size={300}
            subText={false}
            showButton={false}
          />
        )}
        <ReactGlider
          itemWidth={CALENDAR_COLUMN_WIDTH}
          hasArrows={false}
          duration={0}
          cursor={cursorInitial}
          onCursorChange={lazyLoad}
          gliderRef={gliderRef}
        >
          {sortedEvents.map(([dateString, eventsOfDate], index) => {
            const highlightToday = isCurrentDay(dateString);
            return (
              <Stack
                key={dateString}
                ref={index === 0 ? firstEventRef : undefined}
              >
                <OptionalWrapper
                  doWrap={highlightToday}
                  Wrapper={<Stack />}
                  sx={{
                    width: '300px',
                    backgroundColor: theme.palette.background.elevation[12],
                    paddingLeft: '4px',
                    ml: '-4px',
                  }}
                >
                  <CalendarDay
                    date={toLocalMidnightDate(dateString)}
                    events={eventsOfDate}
                    height={height}
                  />
                </OptionalWrapper>
              </Stack>
            );
          })}
        </ReactGlider>
      </Stack>
    </Stack>
  );
}

export const CalendarDaily = memo(CalendarDailyUnmemoized);
